import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BillSummary.css';
import { useAuth } from '../../../contexts/AuthContext';

const BillSummary = () => {
  const [cashBills, setCashBills] = useState([]);
  const [creditBills, setCreditBills] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [debitNotes, setDebitNotes] = useState([]);
  const [filteredCashBills, setFilteredCashBills] = useState([]);
  const [filteredCreditBills, setFilteredCreditBills] = useState([]);
  const [filteredCreditNotes, setFilteredCreditNotes] = useState([]);
  const [filteredDebitNotes, setFilteredDebitNotes] = useState([]);
  const [loading, setLoading] = useState({ cash: false, credit: false, notes: false, debit: false });
  const [errors, setErrors] = useState({ cash: null, credit: null, notes: null, debit: null });
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      return 'mock-token-for-testing';
    }
    
    const lastFirebaseError = localStorage.getItem('firebaseAuthFailed');
    const errorTime = lastFirebaseError ? parseInt(lastFirebaseError) : 0;
    const now = Date.now();
    
    if (now - errorTime < 300000) {
      return 'mock-token-for-testing';
    }
    
    try {
      return await currentUser.getIdToken(true);
    } catch (error) {
      localStorage.setItem('firebaseAuthFailed', now.toString());
      return 'mock-token-for-testing';
    }
  }, [currentUser]);

  const api = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    timeout: 10000,
  }), [API_BASE_URL]);

  api.interceptors.response.use(
    response => response,
    error => {
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );

  const fetchData = useCallback(async (endpoint, setData, type) => {
    if (!currentUser) return;
    
    setLoading(prev => ({ ...prev, [type]: true }));
    setErrors(prev => ({ ...prev, [type]: null }));
    try {
      const token = await getAuthToken();
      const response = await api.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Array.isArray(response.data)) {
        setData(response.data);
      } else if (response.data && response.data.value && Array.isArray(response.data.value)) {
        setData(response.data.value);
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        throw new Error(`Invalid response format for ${type} data`);
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        [type]: `Failed to fetch ${type}: ${err.response?.data?.message || err.message}`
      }));
      setData([]);
    } finally {
      setLoading(prev => ({ ...prev, [type]: false }));
    }
  }, [api, currentUser, getAuthToken]);

  const filterBillsByDate = useCallback((bills, selectedDate) => {
    if (!selectedDate) return bills;
    
    const filterDate = new Date(selectedDate);
    return bills.filter(bill => {
      const billDate = new Date(bill.date || bill.createdAt);
      return billDate.toDateString() === filterDate.toDateString();
    });
  }, []);

  const handleDateFilterChange = (e) => {
    const selectedDate = e.target.value;
    setDateFilter(selectedDate);
  };

  useEffect(() => {
    setFilteredCashBills(filterBillsByDate(cashBills, dateFilter));
    setFilteredCreditBills(filterBillsByDate(creditBills, dateFilter));
    setFilteredCreditNotes(filterBillsByDate(creditNotes, dateFilter));
    setFilteredDebitNotes(filterBillsByDate(debitNotes, dateFilter));
  }, [cashBills, creditBills, creditNotes, debitNotes, dateFilter, filterBillsByDate]);

  useEffect(() => {
    fetchData('/accounts/bill-details/cashBills', setCashBills, 'cash');
    fetchData('/accounts/bill-details/creditBills', setCreditBills, 'credit');
    fetchData('/accounts/bill-details/creditNotes', setCreditNotes, 'notes');
    fetchData('/accounts/bill-details/debitNotes', setDebitNotes, 'debit');
  }, [fetchData]);

  const renderTable = (data, title, type) => {
    const getColumns = (billType) => {
      switch (billType) {
        case 'cash':
        case 'credit':
          return [
            { key: 'invoiceNumber', label: 'Invoice Number' },
            { key: 'customerId', label: 'Customer ID' },
            { key: 'itemname', label: 'Product Name' },
            { key: 'grandTotal', label: 'Total Amount' },
            { key: 'date', label: 'Date' }
          ];
        case 'notes':
          return [
            { key: 'invoiceNumber', label: 'Invoice Number' },
            { key: 'customerId', label: 'Customer ID' },
            { key: 'description', label: 'Description' },
            { key: 'Total', label: 'Total' },
            { key: 'date', label: 'Date' }
          ];
        case 'debit':
          return [
            { key: 'invoiceNumber', label: 'Invoice Number' },
            { key: 'customerId', label: 'Customer ID' },
            { key: 'name', label: 'Product Name' },
            { key: 'totalAmount', label: 'Total Amount' },
            { key: 'date', label: 'Date' }
          ];
        default:
          return [];
      }
    };

    const columns = getColumns(type);

    return (
      <div className="bill-table-wrapper">
        <h2>{title}</h2>
        {loading[type] && <div className="loader">Loading...</div>}
        {errors[type] && <div className="error">{errors[type]}</div>}
        {!loading[type] && !errors[type] && data.length === 0 && (
          <div className="empty">No {title.toLowerCase()} found.</div>
        )}
        {!loading[type] && !errors[type] && data.length > 0 && (
          <table className="bill-table">
            <thead>
              <tr>
                <th>#</th>
                {columns.map(col => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => {
                const getBillTypeInfo = (item, billType) => {
                  switch (billType) {
                    case 'cash':
                      return { type: 'Cash Bill', color: '#e8f5e8', borderColor: '#4caf50' };
                    case 'credit':
                      return { type: 'Credit Bill', color: '#e3f2fd', borderColor: '#2196f3' };
                    case 'notes':
                      return { type: 'Credit Note', color: '#fff3e0', borderColor: '#ff9800' };
                    case 'debit':
                      return { type: 'Debit Note', color: '#fce4ec', borderColor: '#e91e63' };
                    default:
                      return { type: 'Unknown', color: '#f5f5f5', borderColor: '#9e9e9e' };
                  }
                };

                const billInfo = getBillTypeInfo(item, type);
                
                return (
                  <tr 
                    key={`${item.invoiceNumber || item._id || 'unknown'}-${index}`}
                    style={{
                      backgroundColor: billInfo.color,
                      borderLeft: `4px solid ${billInfo.borderColor}`
                    }}
                  >
                    <td>{index + 1}</td>
                    {columns.map(col => (
                      <td key={col.key}>
                        {col.key === 'date' && item[col.key] && item[col.key] !== 'N/A'
                          ? new Date(item[col.key]).toLocaleDateString('en-IN')
                          : col.key === 'grandTotal' || col.key === 'Total' || col.key === 'totalAmount'
                          ? item[col.key] ? `₹${item[col.key].toLocaleString()}` : 'N/A'
                          : item[col.key] || 'N/A'}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div className="bill-summary-container">
      <div className="bill-summary-header">
        <div className="header-left">
          <button
            className="back-button"
            onClick={() => navigate(-1)}
            aria-label="Go Back"
          >
            ← Back
          </button>
          <h1>Bill Summary</h1>
        </div>
        <div className="date-filter-section">
          <label htmlFor="date-filter">
            Filter by Date:
            <input
              type="date"
              id="date-filter"
              value={dateFilter}
              onChange={handleDateFilterChange}
              aria-label="Select date to filter bills"
            />
          </label>
          <button 
            onClick={() => {
              setDateFilter(new Date().toISOString().split('T')[0]);
            }}
            className="today-button"
            aria-label="Show today's bills"
          >
            Today
          </button>
        </div>
      </div>

      {renderTable(filteredCashBills, 'Cash Bills', 'cash')}
      {renderTable(filteredCreditBills, 'Credit Bills', 'credit')}
      {renderTable(filteredCreditNotes, 'Credit Notes', 'notes')}
      {renderTable(filteredDebitNotes, 'Debit Notes', 'debit')}
    </div>
  );
};

export default BillSummary;